/**
 * A pending results-grid "Add to WHERE" for a Query tab's Visual view. The
 * grid posts the cell (its column and value) against the view's resolver
 * document; that document's WHERE condition builder takes it and opens a
 * pre-filled condition draft — the way Table Explorer's "Add to filter" opens
 * one — instead of the grid splicing text into the buffer
 * (`useCellFilterDraftRequest`). A column header's "Add to Filter" posts the
 * column alone, for the Visual view or the SQL view's compact popover.
 * Transient: never persisted.
 */

import { create } from 'zustand';

import type { ResultColumnTarget } from '../utils/resultColumnWhereField';

export type CellFilterDraftRequest = {
  /** The Visual view's resolver document (`vqe-tab:<tabId>`), or the compact popover's (`inline-toolbar:<tabId>`). */
  readonly documentId: string;
  readonly column: ResultColumnTarget;
  /** The cell's value ("Add to WHERE"). */
  readonly value?: unknown;
  /**
   * A column header's "Add to Filter": no cell — the draft opens on the
   * column's default operator with no value. `view` is the Query tab's view.
   */
  readonly columnOnly?: { readonly view: 'visual' | 'sql' };
  /**
   * The buffer offset where the results' statement starts: a builder showing
   * another statement leaves the request (the compact popover follows the
   * caret, which moves there a moment later).
   */
  readonly statementStart?: number;
};

type CellFilterDraftState = {
  readonly request: CellFilterDraftRequest | null;
  /** Post a request; it replaces one no builder has taken yet. */
  readonly requestDraft: (request: CellFilterDraftRequest) => void;
  /** Take `request` if it is still pending — true for exactly one caller. */
  readonly take: (request: CellFilterDraftRequest) => boolean;
};

export const useCellFilterDraftStore = create<CellFilterDraftState>((set, get) => ({
  request: null,
  requestDraft: (request) => set({ request }),
  take: (request) => {
    if (get().request !== request) return false;
    set({ request: null });
    return true;
  },
}));
